// WYSIWYG pipeline: assemble resolution tokens from the sub-turn's
// Soniox tokens + currently-accepted corrections.
//
// Resolution tokens represent what the user sees in the contenteditable:
// pristine Soniox tokens become 1:1 resolution tokens; corrections collapse
// their covered Soniox-token range into a single resolution token whose text
// is the corrected_text.
//
// Used by: assembleResolvedBefore (seed the contenteditable on edit-mode entry)
// and the empty-edit short-circuit in buildWholeSubTurnCorrection (compare
// what the user sees vs. what they typed).

import { canonicalize } from "./canonicalize";
import type { SonioxToken, AcceptedCorrection, ResolutionToken } from "./types";

/**
 * Assemble resolution tokens for a sub-turn.
 *
 * Each Soniox token contributes one resolution token UNLESS an accepted
 * correction covers it - in which case the correction's covered tokens
 * all collapse into a single resolution token whose text is
 * correction.corrected_text.
 *
 * Overlap semantics: half-open [start_ms, end_ms). A Soniox token T is
 * "covered" by a correction C iff T.start_ms >= C.start_ms AND
 * T.end_ms <= C.end_ms (containment).
 */
export function assembleResolutionTokens(
  sonioxTokens: SonioxToken[],
  acceptedCorrections: AcceptedCorrection[],
): ResolutionToken[] {
  const result: ResolutionToken[] = [];
  let charPos = 0;
  let i = 0;

  while (i < sonioxTokens.length) {
    const token = sonioxTokens[i];
    // Find a correction whose range covers this token.
    const coveringCorrection = acceptedCorrections.find(
      (c) => token.start_ms >= c.start_ms && token.end_ms <= c.end_ms,
    );

    if (coveringCorrection) {
      // Find how many subsequent tokens are also covered by this correction.
      let j = i + 1;
      while (
        j < sonioxTokens.length &&
        sonioxTokens[j].start_ms >= coveringCorrection.start_ms &&
        sonioxTokens[j].end_ms <= coveringCorrection.end_ms
      ) {
        j++;
      }
      // Emit one resolution token for the whole correction range.
      const text = coveringCorrection.corrected_text;
      result.push({
        text,
        char_start: charPos,
        char_end: charPos + text.length,
        provenance: {
          start_ms: coveringCorrection.start_ms,
          end_ms: coveringCorrection.end_ms,
          source: "correction",
          correction_id: coveringCorrection.id,
        },
      });
      charPos += text.length;
      i = j;
    } else {
      // Pristine Soniox token.
      result.push({
        text: token.text,
        char_start: charPos,
        char_end: charPos + token.text.length,
        provenance: {
          start_ms: token.start_ms,
          end_ms: token.end_ms,
          source: "soniox",
        },
      });
      charPos += token.text.length;
      i++;
    }
  }

  return result;
}

/**
 * Assemble the resolved-before string from resolution tokens.
 * Canonicalization is applied to the joined string.
 */
export function assembleResolvedBefore(
  sonioxTokens: SonioxToken[],
  acceptedCorrections: AcceptedCorrection[],
): string {
  const tokens = assembleResolutionTokens(sonioxTokens, acceptedCorrections);
  return tokens.map((t) => t.text).join("");
}

/**
 * Assemble the canonicalized verbatim Soniox text for a given range.
 * Used by Step 5b's verbatim-skip check.
 * Half-open [start_ms, end_ms).
 */
export function assembleVerbatim(
  sonioxTokens: SonioxToken[],
  startMs: number,
  endMs: number,
): string {
  const includedTokens = sonioxTokens.filter(
    (t) => t.start_ms >= startMs && t.end_ms <= endMs,
  );
  return canonicalize(includedTokens.map((t) => t.text).join(""));
}
